import type { Dispatch, SetStateAction } from 'react';
import { z } from 'zod';
import type { ILegalPhaseFullObject } from './ILegalPhase';
import type { ILegalRequirementState } from './ILegalRequirementState';

export const logicalOperatorSchema = z.enum(['UND', 'ODER']);
export const ruleTargetTypeSchema = z.enum(['requirement', 'phase']);
export const phaseStatusSchema = z.union([z.literal(0), z.literal(1)]);

export const requirementOptionSchema = z.object({
  targetType: ruleTargetTypeSchema,
  idLegalRequirement: z.number().nullable(),
  idLegalPhase: z.number().nullable(),
  legalTargetLabel: z.string(),
  currentStatusLabel: z.string(),
  legalPhaseLabel: z.string(),
  transactionId: z.number(),
  transactionLabel: z.string(),
  hasError: z.boolean().optional(),
});

export const ruleConditionSchema = z.object({
  id: z.string(),
  idLRDCondition: z.number().optional(),
  transactionId: z.number().nullable(),
  targetType: ruleTargetTypeSchema,
  idLegalRequirement: z.number().nullable(),
  idLegalPhase: z.number().nullable(),
  idLegalRequirementState: z.number().nullable(),
  PhaseIsCompleted: phaseStatusSchema.nullable(),
});

export const ruleConditionGroupSchema = z.object({
  id: z.string(),
  idLRDConditionGroup: z.number().optional(),
  operator: logicalOperatorSchema.nullable(),
  conditions: z.array(ruleConditionSchema),
});

export const ruleActionSchema = z.object({
  id: z.string(),
  idLRDAction: z.number().optional(),
  transactionId: z.number().nullable(),
  targetType: ruleTargetTypeSchema,
  idLegalRequirement: z.number().nullable(),
  idLegalPhase: z.number().nullable(),
  targetStateId: z.number().nullable(),
  TargetPhaseIsCompleted: phaseStatusSchema.nullable(),
});

export const requirementOptionArraySchema = z.array(requirementOptionSchema);
export const ruleConditionArraySchema = z.array(ruleConditionSchema);
export const ruleConditionGroupArraySchema = z.array(ruleConditionGroupSchema);
export const ruleActionArraySchema = z.array(ruleActionSchema);
export const ruleSelectionOptionSchema = z.object({
  id: z.number(),
  label: z.string(),
});
export const builderTransactionOptionSchema = z.object({
  value: z.number(),
  label: z.string(),
});

export const legalRequirementDependencyRuleSchema = z.object({
  idLRDRule: z.number(),
  LegalTransaction_idLegalTransaction: z.number().optional(),
  idLegalTransaction: z.number().optional(),
  Name: z.string().nullable().optional(),
  Executed_at: z.string().nullable().optional(),
  Created_at: z.string().nullable().optional(),
  Updated_at: z.string().nullable().optional(),
  Deleted_at: z.string().nullable().optional(),
});

export const legalRequirementDependencyConditionGroupSchema = z.object({
  idLRDConditionGroup: z.number(),
  LRDRule_idLRDRule: z.number().optional(),
  idLRDRule: z.number().optional(),
  Position: z.number(),
  LogicalOperatorToPrevious: logicalOperatorSchema.nullable().optional(),
  Created_at: z.string().nullable().optional(),
  Updated_at: z.string().nullable().optional(),
});

export const legalRequirementDependencyConditionSchema = z.object({
  idLRDCondition: z.number(),
  LRDConditionGroup_idLRDConditionGroup: z.number().optional(),
  idLRDConditionGroup: z.number().optional(),
  Position: z.number(),
  LegalTransaction_idLegalTransaction: z.number().optional(),
  idLegalTransaction: z.number().optional(),
  TargetType: ruleTargetTypeSchema,
  LegalRequirement_idLegalRequirement: z.number().nullable().optional(),
  idLegalRequirement: z.number().nullable().optional(),
  LegalRequirementState_idLegalRequirementState: z
    .number()
    .nullable()
    .optional(),
  idLegalRequirementState: z.number().nullable().optional(),
  LegalPhase_idLegalPhase: z.number().nullable().optional(),
  idLegalPhase: z.number().nullable().optional(),
  PhaseIsCompleted: z.boolean().nullable().optional(),
  LegalRequirement: z.string().optional(),
  LegalRequirementState: z.string().optional(),
  LegalPhase: z.string().optional(),
  LegalPhaseIsComplete: z.boolean().optional(),
  Created_at: z.string().nullable().optional(),
  Updated_at: z.string().nullable().optional(),
});

export const legalRequirementDependencyActionSchema = z.object({
  idLRDAction: z.number(),
  LRDRule_idLRDRule: z.number().optional(),
  idLRDRule: z.number().optional(),
  Position: z.number(),
  LegalTransaction_idLegalTransaction: z.number().optional(),
  idLegalTransaction: z.number().optional(),
  TargetType: ruleTargetTypeSchema,
  LegalRequirement_idLegalRequirement: z.number().nullable().optional(),
  idLegalRequirement: z.number().nullable().optional(),
  TargetLegalRequirementState_idLegalRequirementState: z
    .number()
    .nullable()
    .optional(),
  idLegalRequirementState: z.number().nullable().optional(),
  LegalPhase_idLegalPhase: z.number().nullable().optional(),
  idLegalPhase: z.number().nullable().optional(),
  TargetPhaseIsCompleted: z.boolean().nullable().optional(),
  LegalRequirement: z.string().optional(),
  TargetLegalRequirementState: z.string().optional(),
  LegalPhase: z.string().optional(),
  LegalPhaseIsComplete: z.boolean().optional(),
  Created_at: z.string().nullable().optional(),
  Updated_at: z.string().nullable().optional(),
});

export const legalRequirementDependencyOverviewEntrySchema = z.object({
  Rule: legalRequirementDependencyRuleSchema,
  ConditionGroupArray: z.array(legalRequirementDependencyConditionGroupSchema),
  ConditionArray: z.array(legalRequirementDependencyConditionSchema),
  ActionArray: z.array(legalRequirementDependencyActionSchema),
});

export const legalRequirementDependencyOverviewSchema = z.object({
  count: z.number(),
  next: z.number().nullable().optional(),
  previous: z.number().nullable().optional(),
  results: z.array(legalRequirementDependencyOverviewEntrySchema),
});

export const legalDependencyLookupLegalTransactionSchema = z.object({
  idLegalTransaction: z.number(),
  Title: z.string().nullable().optional(),
});

export const legalDependencyLookupLegalRequirementSchema = z.object({
  idLegalRequirement: z.number(),
  LegalRequirement: z.string().nullable().optional(),
});

export const legalDependencyLookupLegalPhaseSchema = z.object({
  idLegalPhase: z.number(),
  LegalPhase: z.string().nullable().optional(),
});

export const legalRequirementDependencyByTransactionResponseSchema = z.object({
  idLegalTransaction: z.number(),
  idLegalRequirement: z.number().nullable().optional(),
  includeExecuted: z.boolean().optional(),
  RuleArray: z.array(legalRequirementDependencyRuleSchema),
  ConditionGroupArray: z.array(legalRequirementDependencyConditionGroupSchema),
  ConditionArray: z.array(legalRequirementDependencyConditionSchema),
  ActionArray: z.array(legalRequirementDependencyActionSchema),
  LegalTransactionLookup: z.array(legalDependencyLookupLegalTransactionSchema),
  LegalRequirementLookup: z.array(legalDependencyLookupLegalRequirementSchema),
  LegalPhaseLookup: z.array(legalDependencyLookupLegalPhaseSchema),
});

export const legalDependencyDataSchema = z.object({
  DependencyRuleArray: z.array(legalRequirementDependencyRuleSchema),
  DependencyConditionGroupArray: z.array(
    legalRequirementDependencyConditionGroupSchema
  ),
  DependencyConditionArray: z.array(legalRequirementDependencyConditionSchema),
  DependencyActionArray: z.array(legalRequirementDependencyActionSchema),
  DependencyLegalTransactionLookup: z
    .array(legalDependencyLookupLegalTransactionSchema)
    .optional(),
  DependencyLegalRequirementLookup: z
    .array(legalDependencyLookupLegalRequirementSchema)
    .optional(),
  DependencyLegalPhaseLookup: z
    .array(legalDependencyLookupLegalPhaseSchema)
    .optional(),
});

export type LogicalOperator = z.infer<typeof logicalOperatorSchema>;
export type RuleTargetType = z.infer<typeof ruleTargetTypeSchema>;
export type PhaseStatus = z.infer<typeof phaseStatusSchema>;
export type IRequirementOption = z.infer<typeof requirementOptionSchema>;
export type IRuleCondition = z.infer<typeof ruleConditionSchema>;
export type IRuleConditionGroup = z.infer<typeof ruleConditionGroupSchema>;
export type IRuleAction = z.infer<typeof ruleActionSchema>;
export type IRuleSelectionOption = z.infer<typeof ruleSelectionOptionSchema>;
export type IBuilderTransactionOption = z.infer<
  typeof builderTransactionOptionSchema
>;
export type ILegalRequirementDependencyRule = z.infer<
  typeof legalRequirementDependencyRuleSchema
>;
export type ILegalRequirementDependencyConditionGroup = z.infer<
  typeof legalRequirementDependencyConditionGroupSchema
>;
export type ILegalRequirementDependencyCondition = z.infer<
  typeof legalRequirementDependencyConditionSchema
>;
export type ILegalRequirementDependencyAction = z.infer<
  typeof legalRequirementDependencyActionSchema
>;
export type ILegalRequirementDependencyOverview = z.infer<
  typeof legalRequirementDependencyOverviewSchema
>;
export type ILegalRequirementDependencyOverviewEntry = z.infer<
  typeof legalRequirementDependencyOverviewEntrySchema
>;
export type ILegalDependencyLookupLegalTransaction = z.infer<
  typeof legalDependencyLookupLegalTransactionSchema
>;
export type ILegalDependencyLookupLegalRequirement = z.infer<
  typeof legalDependencyLookupLegalRequirementSchema
>;
export type ILegalDependencyLookupLegalPhase = z.infer<
  typeof legalDependencyLookupLegalPhaseSchema
>;
export type ILegalRequirementDependencyByTransactionResponse = z.infer<
  typeof legalRequirementDependencyByTransactionResponseSchema
>;
export type ILegalDependencyData = z.infer<typeof legalDependencyDataSchema>;
export type ICreateLegalRequirementDependencyRule = Omit<
  ILegalRequirementDependencyRule,
  'idLRDRule' | 'Created_at' | 'Updated_at' | 'Deleted_at'
>;
export type ICreateLegalRequirementDependencyConditionGroup = Omit<
  ILegalRequirementDependencyConditionGroup,
  'idLRDConditionGroup' | 'Created_at' | 'Updated_at'
>;
export type ICreateLegalRequirementDependencyCondition = Omit<
  ILegalRequirementDependencyCondition,
  | 'idLRDCondition'
  | 'LegalRequirement'
  | 'LegalRequirementState'
  | 'LegalPhase'
  | 'LegalPhaseIsComplete'
  | 'Created_at'
  | 'Updated_at'
>;
export type ICreateLegalRequirementDependencyAction = Omit<
  ILegalRequirementDependencyAction,
  | 'idLRDAction'
  | 'LegalRequirement'
  | 'TargetLegalRequirementState'
  | 'LegalPhase'
  | 'LegalPhaseIsComplete'
  | 'Created_at'
  | 'Updated_at'
>;

export const createEmptyLegalDependencyData = (): ILegalDependencyData => ({
  DependencyRuleArray: [],
  DependencyConditionGroupArray: [],
  DependencyConditionArray: [],
  DependencyActionArray: [],
  DependencyLegalTransactionLookup: [],
  DependencyLegalRequirementLookup: [],
  DependencyLegalPhaseLookup: [],
});

export interface ILegalDependencyStateProps {
  legalDependencyData?: ILegalDependencyData;
  setLegalDependencyData?: Dispatch<SetStateAction<ILegalDependencyData>>;
}

export interface ILegalDependencyMockupProps
  extends ILegalDependencyStateProps {
  currentLegalTransactionId?: number;
  currentLegalPhases?: ILegalPhaseFullObject[];
  legalRequirementStates: ILegalRequirementState[];
  initialSelectedRuleId?: number | null;
}

export interface ILegalDependencyEmbeddedStateProps
  extends ILegalDependencyStateProps {
  currentLegalTransactionId?: number;
}

export interface ITargetAutocompleteProps {
  disabled: boolean;
  label: string;
  noOptionsText: string;
  onChange: (value: IRequirementOption | null) => void;
  options: IRequirementOption[];
  placeholder: string;
  value: IRequirementOption | null;
}

export type TLegalDependencyGetRequirementOption = (
  transactionId: number | null,
  targetType: RuleTargetType,
  requirementId: number | null,
  phaseId: number | null
) => IRequirementOption | null;

export type TLegalDependencyGetStatusLabel = (
  targetType: RuleTargetType,
  requirementStateId: number | null,
  phaseIsCompleted: PhaseStatus | null
) => string | null;

export type TLegalDependencyGetTransactionOption = (
  transactionId: number | null
) => IBuilderTransactionOption | null;

export type TLegalDependencyIsTransactionLoading = (
  transactionId: number | null
) => boolean;

export interface ILegalDependencyHeaderProps {
  canSaveRule: boolean;
  isActionToolbarBusy: boolean;
  isDeletePending: boolean;
  isDependenciesLoading: boolean;
  isSavePending: boolean;
  onAddAction: () => void;
  onAddConditionGroup: () => void;
  onDeleteRule: () => void;
  onResetBuilder: () => void;
  onResetToCurrentRule: () => void;
  onRuleNameChange: (ruleName: string) => void;
  onRuleSelectionChange: (ruleId: number | null) => void;
  onSaveRule: () => void;
  ruleName: string;
  ruleSelectionOptions: IRuleSelectionOption[];
  selectedRuleId: number | null;
}

export interface ILegalDependencyConditionRowProps {
  condition: IRuleCondition;
  conditionIndex: number;
  getRequirementOption: TLegalDependencyGetRequirementOption;
  getStatusLabel: TLegalDependencyGetStatusLabel;
  getTransactionOption: TLegalDependencyGetTransactionOption;
  isLoadingLegalTransactions: boolean;
  legalRequirementStates: ILegalRequirementState[];
  onChange: (updates: Partial<IRuleCondition>) => void;
  onRemove: () => void;
  removeDisabled: boolean;
  requirementOptions: IRequirementOption[];
  transactionLoading: boolean;
  transactionOptions: IBuilderTransactionOption[];
}

export interface ILegalDependencyConditionGroupProps {
  getRequirementOption: TLegalDependencyGetRequirementOption;
  getStatusLabel: TLegalDependencyGetStatusLabel;
  getTransactionOption: TLegalDependencyGetTransactionOption;
  group: IRuleConditionGroup;
  groupIndex: number;
  groupLoading: boolean;
  isLoadingLegalTransactions: boolean;
  isTransactionLoading: TLegalDependencyIsTransactionLoading;
  legalRequirementStates: ILegalRequirementState[];
  onAddCondition: () => void;
  onChangeCondition: (
    conditionId: string,
    updates: Partial<IRuleCondition>
  ) => void;
  onChangeOperator: (operator: LogicalOperator) => void;
  onRemoveCondition: (conditionId: string) => void;
  onRemoveGroup: () => void;
  removeGroupDisabled: boolean;
  requirementOptionsByTransaction: Map<number, IRequirementOption[]>;
  transactionOptions: IBuilderTransactionOption[];
}

export interface ILegalDependencyConditionsProps {
  conditionGroups: IRuleConditionGroup[];
  getRequirementOption: TLegalDependencyGetRequirementOption;
  getStatusLabel: TLegalDependencyGetStatusLabel;
  getTransactionOption: TLegalDependencyGetTransactionOption;
  isLoadingLegalTransactions: boolean;
  isTransactionLoading: TLegalDependencyIsTransactionLoading;
  legalRequirementStates: ILegalRequirementState[];
  onAddCondition: (groupId: string) => void;
  onChangeCondition: (
    groupId: string,
    conditionId: string,
    updates: Partial<IRuleCondition>
  ) => void;
  onChangeGroupOperator: (groupId: string, operator: LogicalOperator) => void;
  onRemoveCondition: (groupId: string, conditionId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  requirementOptionsByTransaction: Map<number, IRequirementOption[]>;
  transactionOptions: IBuilderTransactionOption[];
}

export interface ILegalDependencyActionRowProps {
  action: IRuleAction;
  getRequirementOption: TLegalDependencyGetRequirementOption;
  getStatusLabel: TLegalDependencyGetStatusLabel;
  getTransactionOption: TLegalDependencyGetTransactionOption;
  isLoadingLegalTransactions: boolean;
  legalRequirementStates: ILegalRequirementState[];
  onChange: (updates: Partial<IRuleAction>) => void;
  onRemove: () => void;
  removeDisabled: boolean;
  requirementOptions: IRequirementOption[];
  transactionLoading: boolean;
  transactionOptions: IBuilderTransactionOption[];
}

export interface ILegalDependencyActionsProps {
  actions: IRuleAction[];
  getRequirementOption: TLegalDependencyGetRequirementOption;
  getStatusLabel: TLegalDependencyGetStatusLabel;
  getTransactionOption: TLegalDependencyGetTransactionOption;
  isLoadingLegalTransactions: boolean;
  isTransactionLoading: TLegalDependencyIsTransactionLoading;
  legalRequirementStates: ILegalRequirementState[];
  onChangeAction: (actionId: string, updates: Partial<IRuleAction>) => void;
  onRemoveAction: (actionId: string) => void;
  requirementOptionsByTransaction: Map<number, IRequirementOption[]>;
  transactionOptions: IBuilderTransactionOption[];
}

export type TLegalDependencyGetRequirementSummary = (
  transactionId: number | null,
  targetType: RuleTargetType,
  requirementId: number | null,
  phaseId: number | null,
  stateId: number | null,
  phaseIsCompleted: PhaseStatus | null
) => string;

export interface ILegalDependencyPreviewProps {
  actions: IRuleAction[];
  conditionGroups: IRuleConditionGroup[];
  getRequirementSummary: TLegalDependencyGetRequirementSummary;
  getStatusLabel: TLegalDependencyGetStatusLabel;
}
